<script setup>
// Redes sociais: até 8 redes com link. Elemento novo (nenhum link ainda) já
// vem com as redes conectadas da conta; o botão "Preencher" busca de novo.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/marketingEmailTemplates';
import SpacingField from '../../fields/SpacingField.vue';
import SliderField from '../../fields/SliderField.vue';
import AlignField from '../../fields/AlignField.vue';
import VisibilityField from '../../fields/VisibilityField.vue';
import { withScheme } from '../../document/links';
import {
  SOCIAL_NETWORKS,
  ICON_STYLES,
  SOCIAL_MAX,
  hasLink,
  mergeSocialItems,
} from '../../document/social';

const props = defineProps({
  node: { type: Object, required: true },
  selection: { type: Object, default: null },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const busy = ref(false);
const st = computed(() => props.node.style || {});
const items = computed(() => props.node.items || []);
const style = (key, value) => emit('update', { style: { [key]: value } }, key);
const setItems = list => emit('update', { items: list }, 'items');
const setItem = (index, patch) =>
  setItems(
    items.value.map((item, i) => (i === index ? { ...item, ...patch } : item))
  );
const addItem = () => {
  if (items.value.length >= SOCIAL_MAX) return;
  const used = new Set(items.value.map(item => item.network));
  const network = SOCIAL_NETWORKS.find(n => !used.has(n)) || 'site';
  setItems([...items.value, { network, url: '' }]);
};
const removeItem = index => setItems(items.value.filter((_, i) => i !== index));

const fill = async ({ silent = false } = {}) => {
  if (busy.value) return;
  busy.value = true;
  try {
    const { data } = await API.socialLinks();
    const merged = mergeSocialItems(items.value, data.items);
    if (!data.items?.length || !merged.length) {
      if (!silent) useAlert(t('EMAIL_TEMPLATES.EDITOR.SOCIAL.NONE_CONNECTED'));
      return;
    }
    setItems(merged);
  } catch (error) {
    if (!silent) useAlert(t('EMAIL_TEMPLATES.EDITOR.SOCIAL.ERROR'));
  } finally {
    busy.value = false;
  }
};
onMounted(() => {
  if (!items.value.some(item => hasLink(item.url))) fill({ silent: true });
});
const onUrlBlur = (index, value) => {
  const fixed = withScheme(value);
  if (fixed !== value) setItem(index, { url: fixed });
};
const inputClass =
  'rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm';
</script>

<template>
  <div data-props="social" class="flex flex-col gap-4">
    <button
      type="button"
      data-test="fill"
      class="self-start rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-12 disabled:opacity-50"
      :disabled="busy"
      @click="fill()"
    >
      {{ t('EMAIL_TEMPLATES.EDITOR.SOCIAL.FILL') }}
    </button>
    <div class="flex flex-col gap-2">
      <div
        v-for="(item, i) in items"
        :key="i"
        class="flex items-center gap-1.5"
      >
        <select
          :value="item.network"
          :class="inputClass"
          class="w-28 shrink-0"
          :aria-label="t('EMAIL_TEMPLATES.EDITOR.SOCIAL.NETWORK')"
          @change="setItem(i, { network: $event.target.value })"
        >
          <option v-for="n in SOCIAL_NETWORKS" :key="n" :value="n">
            {{ t(`EMAIL_TEMPLATES.EDITOR.SOCIAL.NETWORKS.${n}`) }}
          </option>
        </select>
        <input
          :value="item.url"
          :class="inputClass"
          class="min-w-0 flex-1"
          :placeholder="t('EMAIL_TEMPLATES.EDITOR.SOCIAL.URL')"
          @input="setItem(i, { url: $event.target.value })"
          @blur="onUrlBlur(i, $event.target.value)"
        />
        <button
          type="button"
          :data-test="`remove-${i}`"
          class="i-lucide-x size-4 shrink-0 text-n-slate-10 hover:text-n-ruby-11"
          :title="t('EMAIL_TEMPLATES.EDITOR.SOCIAL.REMOVE')"
          @click="removeItem(i)"
        />
      </div>
      <button
        type="button"
        data-test="add"
        class="self-start text-xs font-medium text-n-brand disabled:opacity-40"
        :disabled="items.length >= SOCIAL_MAX"
        @click="addItem"
      >
        {{ t('EMAIL_TEMPLATES.EDITOR.SOCIAL.ADD') }}
      </button>
    </div>
    <div class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.SOCIAL.ICON_STYLE')
      }}</span>
      <div
        class="flex gap-0.5 self-start rounded-lg border border-n-weak p-0.5"
      >
        <button
          v-for="s in ICON_STYLES"
          :key="s"
          type="button"
          class="rounded-md px-2 py-0.5 text-xs"
          :class="
            st.icon_style === s
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11'
          "
          @click="style('icon_style', s)"
        >
          {{ t(`EMAIL_TEMPLATES.EDITOR.SOCIAL.ICON_STYLES.${s}`) }}
        </button>
      </div>
    </div>
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.SOCIAL.SIZE')"
      :model-value="st.size || 32"
      :min="24"
      :max="48"
      unit="px"
      @update:model-value="v => style('size', v)"
    />
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.SOCIAL.GAP')"
      :model-value="st.gap ?? 12"
      :min="4"
      :max="24"
      unit="px"
      @update:model-value="v => style('gap', v)"
    />
    <AlignField
      :model-value="st.align"
      @update:model-value="v => style('align', v)"
    />
    <SpacingField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.PADDING')"
      :model-value="st.padding"
      @update:model-value="v => style('padding', v)"
    />
    <VisibilityField
      v-if="selection?.row?.role !== 'footer'"
      :model-value="st.hide_on"
      @update:model-value="v => style('hide_on', v)"
    />
  </div>
</template>
